import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { Trans } from 'react-i18next';
import { AmountInput } from '@/components/amount-input';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { serverFieldErrors } from '@/components/settings-section';
import { toast } from '@/components/toast';
import { VatTabs } from '@/components/vat';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { ltr } from '@/lib/arabic';
import { formatTrn, useMe } from '@/lib/clients';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { useVatSettings, type VatSettings } from '@/lib/vat';

export const Route = createFileRoute('/taxes/vat/settings')({ component: VatSettingsPage });

const STAGGERS = [
  { value: 1, label: 'taxPages.vatSettings.stagger1' },
  { value: 2, label: 'taxPages.vatSettings.stagger2' },
  { value: 3, label: 'taxPages.vatSettings.stagger3' },
] as const;

/** The emirates as the API stores them (English names) → their catalogue keys. */
const EMIRATE_KEYS = {
  'Abu Dhabi': 'taxPages.emirates.abuDhabi',
  Dubai: 'taxPages.emirates.dubai',
  Sharjah: 'taxPages.emirates.sharjah',
  Ajman: 'taxPages.emirates.ajman',
  'Umm Al Quwain': 'taxPages.emirates.ummAlQuwain',
  'Ras Al Khaimah': 'taxPages.emirates.rasAlKhaimah',
  Fujairah: 'taxPages.emirates.fujairah',
} as const;

/** VAT centre › Settings (document 05, "VAT centre": third tab). */
function VatSettingsPage() {
  const t = useT();
  const orgId = useOrgId();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const settings = useVatSettings();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<VatSettings | null>(null);
  useEffect(() => {
    if (settings.data) setDraft(settings.data);
  }, [settings.data]);
  const save = useMutation({
    mutationFn: (d: VatSettings) =>
      api<VatSettings>(`/orgs/${orgId}/vat/settings`, {
        method: 'PATCH',
        body: JSON.stringify({
          frequency: d.frequency,
          quarterStartMonth: d.quarterStartMonth,
          largePurchaseThreshold: d.largePurchaseThreshold,
          disclosureThreshold: d.disclosureThreshold,
        }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['vat', orgId] });
      toast(t('taxPages.vatSettings.saved'));
    },
  });
  const errors = serverFieldErrors(save.error);
  const s = settings.data;

  return (
    <>
      <PageHeader
        title={t('taxPages.vatShared.title')}
        description={t('taxPages.vatSettings.description')}
      />
      <VatTabs />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : settings.isPending || !draft ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : settings.isError ? (
        <EmptyState title={t('taxPages.vatSettings.loadError')} hint={settings.error.message} />
      ) : (
        <div className="grid max-w-3xl gap-4">
          <section className="rounded-lg border bg-card p-5">
            <h2 className="mb-3 text-base font-semibold">
              {t('taxPages.vatSettings.registration')}
            </h2>
            <dl className="grid grid-cols-[200px_1fr] gap-y-2 text-sm">
              <dt className="text-muted-foreground">{t('taxPages.vatSettings.vatRegistered')}</dt>
              <dd>{s?.vatRegistered ? t('common.yes') : t('common.no')}</dd>
              <dt className="text-muted-foreground">{t('taxPages.vatShared.trnLabel')}</dt>
              <dd className="tnum">{formatTrn(s?.trn ?? null) || '—'}</dd>
              <dt className="text-muted-foreground">{t('taxPages.vatSettings.effectiveDate')}</dt>
              <dd>{s?.registrationDate ? displayDate(s.registrationDate) : '—'}</dd>
              <dt className="text-muted-foreground">{t('taxPages.vatSettings.emirate')}</dt>
              <dd>
                {s?.emirate
                  ? s.emirate in EMIRATE_KEYS
                    ? t(EMIRATE_KEYS[s.emirate as keyof typeof EMIRATE_KEYS])
                    : s.emirate
                  : '—'}
              </dd>
            </dl>
            <p className="mt-3 text-xs text-muted-foreground">
              <Trans
                i18nKey="taxPages.vatSettings.registrationKept"
                components={{
                  lnk: <Link to="/settings/company" className="text-primary hover:underline" />,
                }}
              />
            </p>
          </section>
          <form
            className="grid gap-4 rounded-lg border bg-card p-5"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              save.mutate(draft);
            }}
          >
            <h2 className="text-base font-semibold">
              {t('taxPages.vatSettings.periodsAndChecks')}
            </h2>
            <ErrorBanner>
              {save.error && Object.keys(errors).length === 0 ? save.error.message : null}
            </ErrorBanner>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={t('taxPages.vatShared.filingFrequency')}
                hint={t('taxPages.vatSettings.frequencyHint')}
              >
                {(id) => (
                  <NativeSelect
                    id={id}
                    disabled={!canEdit}
                    value={draft.frequency}
                    onChange={(e) =>
                      setDraft({ ...draft, frequency: e.target.value as VatSettings['frequency'] })
                    }
                  >
                    <option value="quarterly">{t('taxPages.vatShared.quarterly')}</option>
                    <option value="monthly">{t('taxPages.vatShared.monthly')}</option>
                  </NativeSelect>
                )}
              </Field>
              <Field
                label={t('taxPages.vatSettings.quarters')}
                hint={t('taxPages.vatSettings.quartersHint')}
              >
                {(id) => (
                  <NativeSelect
                    id={id}
                    disabled={!canEdit || draft.frequency === 'monthly'}
                    value={draft.quarterStartMonth}
                    onChange={(e) =>
                      setDraft({ ...draft, quarterStartMonth: Number(e.target.value) })
                    }
                  >
                    {STAGGERS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {t(o.label)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field
                label={t('taxPages.vatSettings.largePurchase', { currency: s?.currency ?? 'AED' })}
                hint={t('taxPages.vatSettings.largePurchaseHint')}
                error={errors['largePurchaseThreshold']}
              >
                {(id) => (
                  <AmountInput
                    id={id}
                    className="h-9 rounded-md border border-input bg-card px-3 text-end text-sm tnum"
                    disabled={!canEdit}
                    value={draft.largePurchaseThreshold}
                    onValueChange={(v) => setDraft({ ...draft, largePurchaseThreshold: v || '0' })}
                  />
                )}
              </Field>
              <Field
                label={t('taxPages.vatSettings.disclosure', { currency: s?.currency ?? 'AED' })}
                hint={t('taxPages.vatSettings.disclosureHint')}
                error={errors['disclosureThreshold']}
              >
                {(id) => (
                  <AmountInput
                    id={id}
                    className="h-9 rounded-md border border-input bg-card px-3 text-end text-sm tnum"
                    disabled={!canEdit}
                    value={draft.disclosureThreshold}
                    onValueChange={(v) => setDraft({ ...draft, disclosureThreshold: v || '0' })}
                  />
                )}
              </Field>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('taxPages.vatSettings.frequencyNote', {
                large: ltr(`${s?.currency ?? ''} ${formatAmount(s?.largePurchaseThreshold)}`),
                disclosure: ltr(`${s?.currency ?? ''} ${formatAmount(s?.disclosureThreshold)}`),
              })}
            </p>
            {canEdit ? (
              <div>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? t('common.saving') : t('common.save')}
                </Button>
              </div>
            ) : null}
          </form>
        </div>
      )}
    </>
  );
}
